import * as React from 'react';

import { illustrations } from '../../assets/illustrations';
import { MaskIcon } from '../../lib/mask-icon';
import { UserTypeDrillButton, type IdentityKind } from '../../lib/user-type-drill';
import { cn } from '../../lib/utils';
import {
  MultiSegmentDonut,
  type MultiSegmentDonutSegment,
} from '../../molecules/MultiSegmentDonut';
import { SensitivityBreakdownRow } from '../../molecules/SensitivityBreakdownRow';

export interface SensitivityBodyTier {
  id: string;
  fillColor: string;
  foregroundColor: string;
}

export interface SensitivityBodyRow {
  id: string;
  label: string;
  files: number;
  hi: number;
  nhi: number;
  external: number;
  /** 0–100. Driver of the donut segment length for this tier. */
  filesPercent: number;
  isHighlighted?: boolean;
}

/**
 * One extra slice and table row after the tiers, for a share the tier set does
 * not cover. Identity counts are optional because a consumer may have none
 * measured for it, and it is not selectable unless the consumer says so.
 */
export interface SensitivityBodyExtraRow {
  id: string;
  label: string;
  files: number;
  /** 0-100. Segment length, on the same scale as a tier's `filesPercent`. */
  filesPercent: number;
  color: string;
  hi?: number;
  nhi?: number;
  external?: number;
  /** Defaults to false: the row is a remainder, not one of the `tiers`. */
  selectable?: boolean;
}

export interface SensitivityBodyProps {
  totalFiles: number;
  totalHI: number;
  totalNHI: number;
  totalExternal: number;
  /** Five rows, one per tier. The component walks `tiers` for render order. */
  rows: readonly SensitivityBodyRow[];
  /** Render-order + color registry. The donut and table walk this array. */
  tiers: readonly SensitivityBodyTier[];
  /** Color applied to non-zero `external` values in the donut center. */
  externalColor: string;
  /** Omitted when there is no remainder to draw; the ring is then tiers-only. */
  extraRow?: SensitivityBodyExtraRow;
  /** Formatter applied to every number on the card. */
  formatNumber: (value: number) => string;
  /** Fires when a row button or its matching donut segment is clicked. */
  onTierSelect: (tierId: string) => void;
  /**
   * A row's HI / NHI / External count click: that row's tier for that identity
   * kind (TDEV-1116). When supplied, each count is its own button.
   */
  onUserTypeClick?: (rowId: string, kind: IdentityKind) => void;
  /** A donut-centre HI / NHI / External total click: that identity kind alone (TDEV-1116). */
  onTotalUserTypeClick?: (kind: IdentityKind) => void;
  className?: string;
}

const FALLBACK_TIER_COLOR = '#D9D9D9';

export function SensitivityBody({
  totalFiles,
  totalHI,
  totalNHI,
  totalExternal,
  rows,
  tiers,
  externalColor,
  extraRow,
  formatNumber,
  onTierSelect,
  onUserTypeClick,
  onTotalUserTypeClick,
  className,
}: SensitivityBodyProps): JSX.Element {
  const rowsById = Object.fromEntries(rows.map((r) => [r.id, r])) as Partial<
    Record<string, SensitivityBodyRow>
  >;
  const tiersById = Object.fromEntries(tiers.map((t) => [t.id, t])) as Partial<
    Record<string, SensitivityBodyTier>
  >;

  const orderedRows = tiers
    .map((t) => rowsById[t.id])
    .filter((r): r is SensitivityBodyRow => r !== undefined);

  const segments: MultiSegmentDonutSegment[] = [
    ...orderedRows.map((row) => ({
      id: row.id,
      percent: row.filesPercent,
      color: tiersById[row.id]?.fillColor ?? FALLBACK_TIER_COLOR,
    })),
    ...(extraRow
      ? [
          {
            id: extraRow.id,
            percent: extraRow.filesPercent,
            color: extraRow.color,
            selectable: extraRow.selectable ?? false,
          },
        ]
      : []),
  ];

  const total = (kind: IdentityKind, content: JSX.Element): JSX.Element =>
    onTotalUserTypeClick ? (
      <UserTypeDrillButton kind={kind} onClick={() => onTotalUserTypeClick(kind)}>
        {content}
      </UserTypeDrillButton>
    ) : (
      content
    );
  const rowUserTypeClick = (rowId: string) =>
    onUserTypeClick ? (kind: IdentityKind) => onUserTypeClick(rowId, kind) : undefined;

  const centerSlot = (
    <div className="flex flex-col items-center gap-[13.2px]">
      <div className="flex items-center gap-1">
        <MaskIcon src={illustrations.fileOutline} size={24} className="text-dawn-black" />
        <span
          data-testid="donut-total-files"
          className="font-outfit text-[26.4px] font-medium leading-[1.2] tabular-nums text-dawn-black"
        >
          {formatNumber(totalFiles)}
        </span>
      </div>
      <div className="flex items-center gap-[8.8px] font-outfit text-[12px] font-normal leading-[1.4]">
        {total(
          'HI',
          <span className="flex items-center gap-1 text-dawn-black">
            <MaskIcon src={illustrations.humanIdentityOutline} size={16} />
            <span data-testid="donut-total-hi" className="tabular-nums">
              {formatNumber(totalHI)}
            </span>
          </span>
        )}
        {totalExternal > 0
          ? total(
              'External',
              <span
                className="flex items-center gap-[2px] font-normal"
                style={{ color: externalColor }}
                data-testid="donut-external-row"
              >
                <MaskIcon src={illustrations.humanIdentityOutline} size={16} />
                <span data-testid="donut-total-external" className="tabular-nums">
                  {formatNumber(totalExternal)}
                </span>
              </span>
            )
          : null}
        {total(
          'NHI',
          <span className="flex items-center gap-1 text-dawn-black">
            <MaskIcon src={illustrations.nonHumanIdentityOutline} size={16} />
            <span data-testid="donut-total-nhi" className="tabular-nums">
              {formatNumber(totalNHI)}
            </span>
          </span>
        )}
      </div>
    </div>
  );

  // The rows sit beside the donut when their content fits there and wrap below
  // it, donut centred, when it does not: the half-width card is ~465px inside
  // at a 1280px viewport, and a 4-digit external count plus "Highly
  // Confidential" need ~355px next to a 240px donut. The card's width, not
  // the viewport's, decides, so the sidebar state is accounted for.
  return (
    <div
      data-testid="sensitivity-body"
      className={cn(
        'flex w-full flex-wrap items-start justify-center gap-9 font-outfit text-dawn-black',
        className
      )}
    >
      <MultiSegmentDonut
        segments={segments}
        centerSlot={centerSlot}
        onSegmentClick={(id) => onTierSelect(id)}
      />

      {/*
       * 9-column subgrid parent: chip(auto) | gap(5px–1fr) | files(auto) |
       * gap(5px–1fr) | nhi(auto) | gap(5px–1fr) | hi(auto) | gap(5px–1fr) | external(auto).
       * All four gap columns are equal 1fr tracks with a 5px floor — they share
       * remaining space evenly and shrink together on narrow screens.
       * `flex-auto` (basis = content width, not 0) is what lets the grid wrap.
       */}
      <div
        data-testid="sensitivity-rows"
        className="grid min-w-0 flex-auto grid-cols-[auto_minmax(5px,1fr)_auto_minmax(5px,1fr)_auto_minmax(5px,1fr)_auto_minmax(5px,1fr)_auto] items-center gap-y-3"
      >
        {orderedRows.map((row, idx) => (
          <React.Fragment key={row.id}>
            {idx > 0 ? (
              <hr
                data-testid="row-divider"
                className="col-span-full m-0 h-0 self-stretch border-0 border-t-[1px] border-grey-100"
              />
            ) : null}
            <div
              className="col-span-full grid grid-cols-subgrid"
              data-testid={`sensitivity-row-${row.id}`}
            >
              <SensitivityBreakdownRow
                level={row.id}
                label={row.label}
                files={row.files}
                hi={row.hi}
                nhi={row.nhi}
                external={row.external}
                onClick={() => onTierSelect(row.id)}
                onUserTypeClick={rowUserTypeClick(row.id)}
              />
            </div>
          </React.Fragment>
        ))}
        {extraRow ? (
          <React.Fragment key={extraRow.id}>
            {orderedRows.length > 0 ? (
              <hr
                data-testid="row-divider"
                className="col-span-full m-0 h-0 self-stretch border-0 border-t-[1px] border-grey-100"
              />
            ) : null}
            <div
              className="col-span-full grid grid-cols-subgrid"
              data-testid={`sensitivity-row-${extraRow.id}`}
            >
              <SensitivityBreakdownRow
                level={extraRow.id}
                label={extraRow.label}
                files={extraRow.files}
                hi={extraRow.hi}
                nhi={extraRow.nhi}
                external={extraRow.external ?? 0}
                onClick={extraRow.selectable === true ? () => onTierSelect(extraRow.id) : undefined}
                onUserTypeClick={
                  extraRow.selectable === true ? rowUserTypeClick(extraRow.id) : undefined
                }
              />
            </div>
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
}

SensitivityBody.displayName = 'SensitivityBody';
